import { decode as decodeHtmlEntities } from 'html-entities';
import _ from 'underscore';
export function decodeXml(str) {
    return decodeHtmlEntities(str);
}
/**
 * @description Does a "light" clean on the specified string:
 * 1) decode html-encoded characters: (e.g. converts &lt; to <);
 * 2) trim the leading and trailing whitespace;
 * 3) removes line ending characters that are invalid in JSON;
 * 4) replaces windows line endings with unix ones
 * 6) replaces multiple consecutive whitespace characters (other than newline) with a single space;
 * 7) split into separate lines and trim leading and trailing whitespace;
 * 8) escape <> to prevent XSS attacks
 * @returns a cleaned up string even if the specified string was null.
 */
export function lightClean(str) {
    return decodeXml(str || '')
        .trim()
        .replace(/\u2028/g, '')
        .replace(/\u2029/g, '')
        .replace(/\r\n/g, '\n')
        .replace(/[^\S\n]{1,}/g, ' ')
        .split('\n')
        .map((line) => {
        return line.trim();
    })
        .join('\n')
        .replace(/</g, '\\<')
        .replace(/>/g, '\\>')
        .trim();
}
/**
 * @description Does a light clean followed by replacing dashes with mdashes.
 * @param {String} str
 * @returns a cleaned up string even if the specified string was null.
 */
export function clean(str) {
    // replace just the dash and preserve the type of whitespace that was there before
    return lightClean(str).replace(/(\s)-{1,2}(\s)/gi, '$1—$2');
}
// Returns a masked representation of the string where all but the first and the last characters are
// replaced with a *. Values of four digits or fewer, or any value when fullyMask is set, become
// all *s.
export function maskString(input, fullyMask) {
    if (!_.isString(input)) {
        return '***not-a-string***';
    }
    if (input.length < 1) {
        return '';
    }
    // Keeping the first and last digit would reveal half of a short PIN.
    if (fullyMask || (input.length <= 4 && /^\d+$/.test(input))) {
        return '*'.repeat(input.length);
    }
    // If the string is all numbers, and more than 11 digits, mask all but the first 6 and last 4
    if (/^\d+$/.test(input) && input.length > 11) {
        const firstSix = input.substring(0, 6);
        const lastFour = input.substring(input.length - 4);
        const middleMasked = '*'.repeat(input.length - 10);
        return `${firstSix}${middleMasked}${lastFour}`;
    }
    let mask = input.charAt(0);
    mask += '*'.repeat(input.length - 1);
    if (input.length > 1) {
        mask += input.charAt(input.length - 1);
    }
    return mask;
}
export function maskEmail(email) {
    if (!_.isString(email)) {
        return '***not-a-string***';
    }
    const parts = email.split('@');
    const username = parts.shift() || '';
    return `${maskString(username)}${parts.length > 0 ? '@' : ''}${parts.join('')}`;
}
export function generateRandomString(stringLength, validCharacterString) {
    let text = '';
    for (let i = 0; i < stringLength; i++) {
        text += validCharacterString.charAt(Math.floor(Math.random() * validCharacterString.length));
    }
    return text;
}
export function randomHexString(stringLength) {
    return generateRandomString(stringLength, 'abcdef0123456789');
}
// supports only 10 digit phone numbers, output is for US/CAN numbers
export function toFormattedPhoneNumber(phoneNumber) {
    if (!_.isString(phoneNumber) || _.size(phoneNumber) !== 10) {
        return phoneNumber || '';
    }
    const areaCode = phoneNumber.slice(0, 3);
    const centralOfficeCode = phoneNumber.slice(3, 6);
    const stationNumber = phoneNumber.slice(6, 10);
    return `(${areaCode}) ${centralOfficeCode}-${stationNumber}`;
}
export function getFirstAndLastNameFromFullName(fullName) {
    if (!fullName) {
        return {
            fullName: undefined,
            firstName: undefined,
            lastName: undefined,
        };
    }
    const splitGuestName = fullName.split(' ');
    const lastNameIndex = splitGuestName.length - 1;
    const firstName = lastNameIndex === 0 ? fullName : splitGuestName.slice(0, lastNameIndex).join(' ');
    const lastName = lastNameIndex === 0 ? '|' : splitGuestName[lastNameIndex] || '|';
    return {
        fullName,
        firstName,
        lastName,
    };
}
export function maskHeaders(incomingHeaders, safeForLoggingHeaderKeySet) {
    return Object.entries(incomingHeaders || {}).reduce((headers, [key, value]) => {
        return {
            ...headers,
            [key]: safeForLoggingHeaderKeySet[key.toLowerCase()] || value === undefined
                ? value
                : maskString(value.toString()),
        };
    }, {});
}
export function maskBodyFields(data, bodyFieldsToMask, safeForLoggingHeaderKeySet = {}) {
    if (Object.keys(bodyFieldsToMask).length === 0) {
        // Nothing to mask
        return data;
    }
    if (data === null || data === undefined) {
        // Nothing to mask
        return data;
    }
    if (Array.isArray(data)) {
        // Handle arrays by recursively masking each element
        return data.map((item) => maskBodyFields(item, bodyFieldsToMask));
    }
    if (!(typeof data === 'object')) {
        // We mask on the object level so primitives we can just return
        return data;
    }
    // Handle objects by checking keys
    const result = {};
    for (const [key, value] of Object.entries(data)) {
        if (value === null || value === undefined) {
            // Nothing to mask
            result[key] = value;
        }
        else if (key === 'headers') {
            // If we use the proxy the actual headers are in the body
            result[key] = maskHeaders(value, safeForLoggingHeaderKeySet);
        }
        else if (Array.isArray(value) &&
            (bodyFieldsToMask[key] || bodyFieldsToMask[key.toLowerCase()])) {
            // If the key is in the bodyFieldsToMask, we need to mask the array
            result[key] = value.map((item) => {
                if (typeof item === 'object') {
                    return maskBodyFields(item, bodyFieldsToMask, safeForLoggingHeaderKeySet);
                }
                return maskString(String(item));
            });
        }
        else if (typeof value === 'object') {
            // Recursively mask nested objects
            result[key] = maskBodyFields(value, bodyFieldsToMask, safeForLoggingHeaderKeySet);
        }
        else if (bodyFieldsToMask[key] || bodyFieldsToMask[key.toLowerCase()]) {
            // Mask this field
            result[key] = maskString(String(value));
        }
        else {
            // Leave this field as is
            result[key] = value;
        }
    }
    return result;
}
export function maskPan(cardNumber) {
    if (cardNumber.length < 16) {
        const unmaskedLength = 4;
        const maskedLength = cardNumber.length - unmaskedLength;
        return Array(maskedLength + 1).join('*') + cardNumber.slice(-unmaskedLength);
    }
    const middle = cardNumber.slice(6, cardNumber.length - 4);
    const redacted = Array(middle.length + 1).join('*');
    const firstSix = cardNumber.slice(0, 6);
    // last 4 digits that are not part of the first 6
    const lastFour = cardNumber.slice(redacted.length + firstSix.length);
    return `${firstSix}${redacted}${lastFour}`;
}
//# sourceMappingURL=universal_string_helper.js.map